// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import mobiusIconsObject from "@insite/mobius/Icons/commonIcons";
import Toaster from "@insite/mobius/Toast/Toaster";
import ToasterContext, { useToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { SectionContainer, SectionTitle } from "@steiner/App/Widgets/DesignSystem/DesignSystem.helpers";
import { clipboardText } from "@steiner/App/Widgets/DesignSystem/Utilities";
import capitalize from "lodash/capitalize";
import React, { FC } from "react";
import { css } from "styled-components";

import xIconsObject from "@steiner/XDL/Icons/xngCommonIcons";

const IconsItem = ({ iconSrc, name, styles }: { iconSrc: any; name: string; styles: IconItemStyles }) => {
    const toaster = useToaster();
    return (
        <Clickable
            onClick={() => {
                const isCopied = clipboardText(name);
                toaster.addToast({
                    body: isCopied ? (
                        <Typography>
                            <strong>{name}</strong> icon is copied
                        </Typography>
                    ) : (
                        <Typography>
                            copy <strong>{name}</strong> icon failed
                        </Typography>
                    ),
                    messageType: isCopied ? "success" : "danger",
                });
            }}
            aria-label="Icons Tab"
        >
            <StyledWrapper {...styles.container}>
                <Icon src={iconSrc} {...styles.icon} />
                <Typography {...styles.title}>{name}</Typography>
            </StyledWrapper>
        </Clickable>
    );
};

interface IconItemStyles {
    container?: InjectableCss;
    icon?: IconPresentationProps;
    title?: TypographyPresentationProps;
}

interface IconsTabStyles {
    titleGridItem?: GridItemProps;
    gridItem?: GridItemProps;
    pathClickable?: ClickableProps;
    pathTypography?: TypographyPresentationProps;
    iconsContainer?: InjectableCss;
    iconItem: IconItemStyles;
}

const iconsTabStyles: IconsTabStyles = {
    titleGridItem: {
        width: 12,
    },
    gridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            flex-wrap: wrap;
        `,
    },
    pathClickable: {
        css: css`
            width: auto;
            &:focus {
                outline: none;
            }
        `,
    },
    pathTypography: {
        size: 15,
        css: css`
            margin-left: 0;
            margin-bottom: 15px;
            background: #f4f4f4;
            color: #666;
            line-height: 1.6;
            max-width: 100%;
            overflow: auto;
            padding: 15px 22px;
            display: block;
            word-wrap: break-word;
            user-select: none;
            border: 1px solid #ddd;
            border-left: 3px solid #f36d33;
        `,
    },
    iconsContainer: {
        css: css`
            display: flex;
            gap: 50px;
            flex-wrap: wrap;
            justify-content: flex-start;
            padding: 15px 0;
        `,
    },
    iconItem: {
        container: {
            css: css`
                overflow-wrap: break-word;
                width: 135px;
                display: flex;
                flex-direction: column;

                border-radius: 8px;
                box-shadow: rgb(0 58 99 / 15%) 0 0 9px 0;
                transition: box-shadow ease-in 150ms;

                & > div {
                    border-top-left-radius: 8px;
                    border-top-right-radius: 8px;
                    border-bottom: 1px solid #cccccc96;
                }
                &:hover {
                    box-shadow: rgb(0 58 99 / 30%) 0 0 15px 0;
                }
            `,
        },
        icon: {
            css: css`
                margin-top: 15px;
                justify-content: center;
                &,
                & svg {
                    height: 24px;
                    width: initial;
                }
            `,
        },
        title: {
            size: 14,
            css: css`
                color: #212121;
                display: block;
                margin-top: 20px;
                padding: 0 8px;
                margin-bottom: 15px;
                text-align: center;
            `,
        },
    },
};

const styles = iconsTabStyles;
const IconsTab: FC = props => {
    // TODO: Change blueprint name
    const blueprintsIcons = [
        {
            path: "@steiner/XDL/Icons",
            icons: xIconsObject,
            name: "Spire Lab",
        },
        {
            path: "@insite/mobius/Icons",
            icons: mobiusIconsObject,
            name: "mobius",
        },
    ];

    return (
        <SectionContainer>
            <Toaster position="bottom" mobilePosition="bottom">
                <ToasterContext.Consumer>
                    {({ addToast }) => (
                        <>
                            {blueprintsIcons.map(blueprintIcons => {
                                if (Object.keys(blueprintIcons.icons).length === 0) {
                                    return null;
                                }

                                return (
                                    <>
                                        <GridItem {...styles.titleGridItem}>
                                            <SectionTitle>{`${capitalize(blueprintIcons.name)} Icons`}</SectionTitle>
                                        </GridItem>
                                        <GridItem {...styles.gridItem} key={blueprintIcons.path}>
                                            <Clickable
                                                {...styles.pathClickable}
                                                onClick={() => {
                                                    const isCopied = clipboardText(blueprintIcons.path);
                                                    addToast({
                                                        body: isCopied
                                                            ? `Import path is copied`
                                                            : `Copy Import path failed.`,
                                                        messageType: isCopied ? "success" : "danger",
                                                    });
                                                }}
                                                aria-label="Icons Tab"
                                            >
                                                <Typography {...styles.pathTypography}>
                                                    {blueprintIcons.path}
                                                </Typography>
                                            </Clickable>
                                            <StyledWrapper {...styles.iconsContainer}>
                                                {Object.keys(blueprintIcons.icons).map(iconName => (
                                                    <IconsItem
                                                        key={iconName}
                                                        iconSrc={blueprintIcons.icons[iconName]}
                                                        name={iconName}
                                                        styles={styles.iconItem}
                                                    />
                                                ))}
                                            </StyledWrapper>
                                        </GridItem>
                                    </>
                                );
                            })}
                        </>
                    )}
                </ToasterContext.Consumer>
            </Toaster>
        </SectionContainer>
    );
};

export default IconsTab;
